// src/hooks/useKioskMode.js
// ─────────────────────────────────────────────────────────────
//  Kiosk lockdown hook
//  - Fullscreen API (hides browser chrome)
//  - Blocks right-click, F12, keyboard shortcuts
//  - Auto-reset cart + return to home after idle timeout
//  - Prevents back/forward navigation
//  - Blocks accidental zoom
// ─────────────────────────────────────────────────────────────

import { useEffect, useCallback, useRef } from 'react';

const IDLE_TIMEOUT_MS = 60 * 1000; // 60 seconds of no interaction → reset

export default function useKioskMode({ enabled = true, onReset, onIdle } = {}) {
  const idleTimer   = useRef(null);
  const countdownRef = useRef(null);

  // ── Fullscreen ─────────────────────────────────────────────
  const enterFullscreen = useCallback(() => {
    const el = document.documentElement;
    if      (el.requestFullscreen)       el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.mozRequestFullScreen)    el.mozRequestFullScreen();
    else if (el.msRequestFullscreen)     el.msRequestFullscreen();
  }, []);

  const exitFullscreen = useCallback(() => {
    if      (document.exitFullscreen)       document.exitFullscreen();
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    else if (document.mozCancelFullScreen)  document.mozCancelFullScreen();
  }, []);

  const isFullscreen = () =>
    !!(document.fullscreenElement
    || document.webkitFullscreenElement
    || document.mozFullScreenElement);

  // ── Idle timer ─────────────────────────────────────────────
  const resetIdleTimer = useCallback(() => {
    clearTimeout(idleTimer.current);
    clearTimeout(countdownRef.current);
    idleTimer.current = setTimeout(() => {
      onIdle?.();
      onReset?.();
    }, IDLE_TIMEOUT_MS);
  }, [onIdle, onReset]);

  // ── Keyboard blocker ───────────────────────────────────────
  const blockKeys = useCallback((e) => {
    const blocked = [
      e.key === 'F12',                          // DevTools
      e.key === 'F5',                           // Refresh
      e.key === 'F11',                          // Fullscreen toggle
      e.altKey && e.key === 'F4',               // Close window
      e.ctrlKey && e.key === 'w',               // Close tab
      e.ctrlKey && e.key === 'r',               // Refresh
      e.ctrlKey && e.key === 't',               // New tab
      e.ctrlKey && e.key === 'n',               // New window
      e.ctrlKey && e.shiftKey && e.key === 'I', // DevTools
      e.ctrlKey && e.shiftKey && e.key === 'J', // Console
      e.ctrlKey && e.shiftKey && e.key === 'C', // Inspector
      e.ctrlKey && e.key === 'u',               // View source
      e.ctrlKey && e.key === '+',               // Zoom in
      e.ctrlKey && e.key === '-',               // Zoom out
      e.ctrlKey && e.key === '0',               // Reset zoom
      e.key === 'Escape' && isFullscreen(),     // Exit fullscreen
    ];
    if (blocked.some(Boolean)) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
  }, []);

  // ── Context menu blocker ───────────────────────────────────
  const blockContextMenu = useCallback((e) => {
    e.preventDefault();
    return false;
  }, []);

  // ── Touch zoom blocker ─────────────────────────────────────
  const blockTouchZoom = useCallback((e) => {
    if (e.touches.length > 1) e.preventDefault();
  }, []);

  // ── History manipulation — prevent back navigation ─────────
  const blockNavigation = useCallback(() => {
    window.history.pushState(null, '', window.location.href);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Enter fullscreen on first user interaction (browsers require gesture)
    const handleFirstInteraction = () => {
      if (!isFullscreen()) enterFullscreen();
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
    document.addEventListener('click',      handleFirstInteraction);
    document.addEventListener('touchstart', handleFirstInteraction);

    // Block keyboard shortcuts
    document.addEventListener('keydown',     blockKeys,       { capture: true });
    document.addEventListener('contextmenu', blockContextMenu, { capture: true });
    document.addEventListener('touchstart',  blockTouchZoom,  { passive: false });

    // Idle tracking — reset on any interaction
    const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    EVENTS.forEach((ev) => document.addEventListener(ev, resetIdleTimer, { passive: true }));
    resetIdleTimer(); // start the timer

    // Block back navigation
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', blockNavigation);

    // Re-enter fullscreen if user somehow exits
    document.addEventListener('fullscreenchange', () => {
      if (!isFullscreen() && enabled) {
        setTimeout(enterFullscreen, 500);
      }
    });

    // Prevent pinch zoom on mobile
    document.querySelector('meta[name=viewport]')?.setAttribute(
      'content',
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no'
    );

    return () => {
      document.removeEventListener('keydown',     blockKeys,       { capture: true });
      document.removeEventListener('contextmenu', blockContextMenu, { capture: true });
      document.removeEventListener('touchstart',  blockTouchZoom);
      EVENTS.forEach((ev) => document.removeEventListener(ev, resetIdleTimer));
      window.removeEventListener('popstate', blockNavigation);
      clearTimeout(idleTimer.current);
    };
  }, [enabled, blockKeys, blockContextMenu, blockTouchZoom, resetIdleTimer, blockNavigation, enterFullscreen]);

  return { enterFullscreen, exitFullscreen, isFullscreen };
}
